import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit, Calendar, Upload, X, Image as ImageIcon,
  Eye, EyeOff, Globe, ExternalLink, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_EVENT = {
  title: '', subtitle: '', description: '', long_description: '',
  image_url: '', gallery: [], location: '', year: '', date: '',
  is_active: true,
};

const ImageUploader = ({ value, onChange, label = "Fotoğraf" }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState(value ? 'preview' : 'upload'); // 'upload' | 'url' | 'preview'
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir görsel dosyası seçin');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan küçük olmalıdır');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Göreceli URL ise tam URL'ye çevir
      let imageUrl = res.data.url;
      if (imageUrl.startsWith('/api/')) {
        imageUrl = `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
      }
      onChange(imageUrl);
      setMode('preview');
      toast.success('Fotoğraf yüklendi');
    } catch (err) {
      toast.error('Fotoğraf yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  const clearImage = () => {
    onChange('');
    setMode('upload');
  };

  if (mode === 'preview' && value) {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
          <img
            src={value}
            alt="Önizleme"
            className="w-full h-52 object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              type="button"
              onClick={() => setMode('url')}
              className="bg-white/90 backdrop-blur-sm text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-white shadow-sm flex items-center gap-1.5"
            >
              <Edit className="w-3 h-3" />
              Değiştir
            </button>
            <button
              type="button"
              onClick={clearImage}
              className="bg-red-500/90 backdrop-blur-sm text-white px-2 py-1.5 rounded-lg text-xs hover:bg-red-600 shadow-sm"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="absolute bottom-2 left-2">
            <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Fotoğraf hazır
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'url') {
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
          <div className="flex gap-2 mb-2">
            <button type="button" onClick={() => setMode('upload')}
              className="flex-1 py-2 rounded-lg text-sm font-medium border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center gap-2">
              <Upload className="w-4 h-4" /> Dosya Yükle
            </button>
            <button type="button"
              className="flex-1 py-2 rounded-lg text-sm font-medium border border-blue-500 text-blue-600 bg-blue-50 flex items-center justify-center gap-2">
              <Globe className="w-4 h-4" /> URL Gir
            </button>
          </div>
          <Input
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              if (e.target.value) setMode('preview');
            }}
            placeholder="https://example.com/image.jpg"
          />
          {value && (
            <Button type="button" size="sm" variant="outline" onClick={() => setMode('preview')} className="w-full">
              Önizle
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Upload mode
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div
        className={`border-2 border-dashed rounded-xl transition-all cursor-pointer ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/30'}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="py-8 text-center">
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Yükleniyor...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center">
                <Upload className="w-7 h-7 text-blue-500" />
              </div>
              <div>
                <p className="font-semibold text-gray-700 text-sm">Fotoğraf yüklemek için tıklayın</p>
                <p className="text-xs text-gray-400 mt-1">veya sürükleyip bırakın</p>
                <p className="text-xs text-gray-400">JPG, PNG, WebP • Max 10MB</p>
              </div>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files[0])}
        />
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400">veya</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setMode('url'); }}
        className="w-full py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-2 transition-colors"
      >
        <Globe className="w-4 h-4" />
        URL ile fotoğraf ekle
      </button>
    </div>
  );
};

const EventCard = ({ event, onEdit, onDelete, onToggleActive }) => (
  <div className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
    {/* Image */}
    <div className="relative h-48 bg-gradient-to-br from-blue-100 to-indigo-100 overflow-hidden">
      {event.image_url ? (
        <img
          src={event.image_url}
          alt={event.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <ImageIcon className="w-12 h-12 text-blue-300" />
        </div>
      )}
      {/* Active badge */}
      <div className="absolute top-3 left-3">
        <button
          onClick={() => onToggleActive(event)}
          className={`px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-all ${event.is_active ? 'bg-green-500 text-white' : 'bg-gray-400 text-white'}`}
        >
          {event.is_active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          {event.is_active ? 'Yayında' : 'Gizli'}
        </button>
      </div>
      {/* Action buttons - visible on hover */}
      <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onEdit(event)}
          className="w-8 h-8 bg-white rounded-lg shadow-md flex items-center justify-center hover:bg-blue-50 transition-colors"
          title="Düzenle"
        >
          <Edit className="w-4 h-4 text-blue-600" />
        </button>
        <button
          onClick={() => onDelete(event.id)}
          className="w-8 h-8 bg-white rounded-lg shadow-md flex items-center justify-center hover:bg-red-50 transition-colors"
          title="Sil"
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </div>

    {/* Content */}
    <div className="p-4">
      <h3 className="font-bold text-gray-900 text-base leading-tight mb-2 line-clamp-2">{event.title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-3">{event.description}</p>
      {event.date && (
        <div className="flex items-center gap-1.5 text-xs text-gray-400">
          <Calendar className="w-3.5 h-3.5" />
          <span>{event.date}</span>
        </div>
      )}
    </div>

    {/* Footer */}
    <div className="px-4 pb-4 flex gap-2">
      <Button size="sm" variant="outline" onClick={() => onEdit(event)} className="flex-1 text-xs">
        <Edit className="w-3 h-3 mr-1.5" />
        Düzenle
      </Button>
      <Button size="sm" variant="outline" onClick={() => onDelete(event.id)} className="text-red-500 border-red-200 hover:bg-red-50 text-xs">
        <Trash2 className="w-3 h-3" />
      </Button>
    </div>
  </div>
);

const EventFormModal = ({ open, onClose, initialData, onSubmit, title }) => {
  const [form, setForm] = useState(initialData || EMPTY_EVENT);

  React.useEffect(() => {
    setForm(initialData || EMPTY_EVENT);
  }, [initialData, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(form);
  };

  const handleGalleryAdd = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir görsel dosyası seçin');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya 10MB altında olmalı');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      let url = res.data.url;
      if (url.startsWith('/api/')) url = `${process.env.REACT_APP_BACKEND_URL}${url}`;
      setForm({ ...form, gallery: [...(form.gallery || []), url] });
      toast.success('Galeri fotoğrafı eklendi');
    } catch {
      toast.error('Yüklenemedi');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 pt-2">

          <ImageUploader
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            label="Kapak Fotoğrafı *"
          />

          <div className="space-y-1.5">
            <Label>Organizasyon Adı *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Örn: Kurumsal Voleybol Turnuvası 2025"
              required
              className="h-11"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Alt Başlık (Opsiyonel) <span className="text-xs text-gray-400 font-normal">— hero altında görünür</span></Label>
            <Input
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              placeholder="Örn: 32 takımlı şehirler arası lig organizasyonu"
              className="h-11"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Kısa Açıklama * <span className="text-xs text-gray-400 font-normal">— kart ve giriş paragrafı (1-2 cümle)</span></Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Organizasyonun kısa özeti..."
              rows={2}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label>Detaylı Anlatım (Opsiyonel) <span className="text-xs text-gray-400 font-normal">— Markdown destekler (## başlık, **kalın**, - liste)</span></Label>
            <Textarea
              value={form.long_description}
              onChange={(e) => setForm({ ...form, long_description: e.target.value })}
              placeholder={'## Etkinlik Hakkında\n\nDetaylar...\n\n## Sonuçlar\n\n- Toplam katılımcı: 500+\n- Süre: 2 gün'}
              rows={8}
              className="font-mono text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Yıl / Dönem</Label>
              <Input
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                placeholder="2025 veya 2024 — 2026"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Lokasyon</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="İstanbul"
                className="h-11"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Tarih (Opsiyonel)</Label>
            <Input
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              placeholder="Örn: Mart 2025 veya 15.03.2025"
              className="h-11"
            />
          </div>

          {/* Gallery */}
          <div className="space-y-2">
            <Label>Galeri Fotoğrafları (Opsiyonel) <span className="text-xs text-gray-400 font-normal">— detay sayfasında galeri olarak gösterilir</span></Label>
            {form.gallery && form.gallery.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-2">
                {form.gallery.map((img, i) => (
                  <div key={i} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
                    <img src={img} alt={`Galeri ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, gallery: form.gallery.filter((_, j) => j !== i) })}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Kaldır"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="block border-2 border-dashed border-gray-200 rounded-xl py-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all">
              <Upload className="w-6 h-6 mx-auto text-gray-400 mb-1" />
              <p className="text-xs text-gray-500">Galeri fotoğrafı ekle (tek tek)</p>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => { handleGalleryAdd(e.target.files?.[0]); e.target.value = ''; }}
              />
            </label>
          </div>

          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <div
              className={`relative w-11 h-6 rounded-full cursor-pointer transition-colors ${form.is_active ? 'bg-blue-500' : 'bg-gray-300'}`}
              onClick={() => setForm({ ...form, is_active: !form.is_active })}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.is_active ? 'left-6' : 'left-1'}`} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Web sitesinde yayınla</p>
              <p className="text-xs text-gray-400">Aktif olduğunda ana sayfada görünür</p>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              İptal
            </Button>
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 h-11">
              {title.includes('Ekle') ? (
                <><Plus className="w-4 h-4 mr-2" />Ekle</>
              ) : (
                <><CheckCircle className="w-4 h-4 mr-2" />Güncelle</>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EventsTab = ({ events, fetchEvents }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showPreview, setShowPreview] = useState(null);

  const handleCreate = async (form) => {
    try {
      await axios.post(`${API}/events`, form);
      toast.success('Organizasyon eklendi ve yayınlandı');
      setShowAddModal(false);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eklenemedi');
    }
  };

  const handleUpdate = async (form) => {
    try {
      await axios.put(`${API}/events/${editingEvent.id}`, form);
      toast.success('Organizasyon güncellendi');
      setShowEditModal(false);
      setEditingEvent(null);
      fetchEvents();
    } catch (err) {
      toast.error('Güncellenemedi');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Bu organizasyonu silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/events/${eventId}`);
      toast.success('Organizasyon silindi');
      fetchEvents();
    } catch (err) {
      toast.error('Silinemedi');
    }
  };

  const handleToggleActive = async (event) => {
    try {
      await axios.put(`${API}/events/${event.id}`, { ...event, is_active: !event.is_active });
      toast.success(event.is_active ? 'Organizasyon gizlendi' : 'Organizasyon yayına alındı');
      fetchEvents();
    } catch (err) {
      toast.error('Güncellenemedi');
    }
  };

  const activeCount = events.filter(e => e.is_active).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Organizasyonlarımız</h2>
          <p className="text-gray-500 text-sm mt-1">
            Ana sayfada gösterilecek organizasyonları yönetin
            {events.length > 0 && (
              <span className="ml-2 text-blue-600 font-medium">
                · {activeCount} yayında, {events.length - activeCount} gizli
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 hover:bg-blue-700 h-11 px-5"
          data-testid="add-event-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Organizasyon
        </Button>
      </div>

      {/* Stats bar */}
      {events.length > 0 && (
        <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full" />
            <span className="text-sm text-gray-700">{activeCount} yayında</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-400 rounded-full" />
            <span className="text-sm text-gray-700">{events.length - activeCount} gizli</span>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1.5 font-medium"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Siteyi Görüntüle
          </a>
        </div>
      )}

      {/* Grid */}
      {events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-20 h-20 bg-blue-50 rounded-3xl flex items-center justify-center mb-5">
            <ImageIcon className="w-10 h-10 text-blue-400" />
          </div>
          <h3 className="text-lg font-bold text-gray-700 mb-2">Henüz organizasyon eklenmemiş</h3>
          <p className="text-gray-400 text-sm max-w-xs mb-6">
            İlk organizasyonunuzu ekleyin ve ana sayfanızda fotoğraflı galeriye sahip olun.
          </p>
          <Button onClick={() => setShowAddModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            İlk Organizasyonu Ekle
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {events.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              onEdit={(e) => { setEditingEvent(e); setShowEditModal(true); }}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
            />
          ))}
        </div>
      )}

      {/* Add Modal */}
      <EventFormModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        initialData={EMPTY_EVENT}
        onSubmit={handleCreate}
        title="Yeni Organizasyon Ekle"
      />

      {/* Edit Modal */}
      <EventFormModal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingEvent(null); }}
        initialData={editingEvent}
        onSubmit={handleUpdate}
        title="Organizasyonu Düzenle"
      />
    </div>
  );
};

export default EventsTab;
